import { refreshTokenApi } from "@/api/services/admin/auth";
import { AppRoutes } from "@/routes/config";
import { removeToken } from "@/utils/token";
import Cookies from "js-cookie";

type TokenChangeCallback = (token: string | null) => void;

class AuthTokenManager {
  private static instance: AuthTokenManager;
  private listeners: Set<TokenChangeCallback> = new Set();
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  private constructor() {}

  static getInstance(): AuthTokenManager {
    if (!AuthTokenManager.instance) {
      AuthTokenManager.instance = new AuthTokenManager();
    }
    return AuthTokenManager.instance;
  }

  getToken(): string | null {
    return Cookies.get("admin-token") || null;
  }

  onTokenChange(callback: TokenChangeCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyTokenChange(token: string | null): void {
    this.listeners.forEach((cb) => cb(token));
  }

  async refreshToken(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<string | null> {
    const email = Cookies.get("admin-email");
    const refreshToken = Cookies.get("admin-refresh-token");

    if (!email || !refreshToken) {
      this.clearTokens();
      window.location.replace(AppRoutes.login);
      return null;
    }

    try {
      await refreshTokenApi({ refreshToken, email });
      const newToken = this.getToken();
      this.notifyTokenChange(newToken);
      return newToken;
    } catch {
      this.clearTokens();
      window.location.replace(AppRoutes.login);
      return null;
    }
  }

  clearTokens(): void {
    removeToken();
    Cookies.remove("admin-user-id");
    Cookies.remove("admin-email");
    this.notifyTokenChange(null);
  }

  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }
}

export const authTokenManager = AuthTokenManager.getInstance();
